import React, { useCallback, useEffect, useRef } from 'react';
import ReactFlow, {
  Background,
  Controls,
  Handle,
  MarkerType,
  MiniMap,
  Position,
  ReactFlowProvider,
  applyNodeChanges,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Connection,
  type Edge,
  type Node,
  type NodeChange,
  type NodeProps,
} from 'reactflow';
import 'reactflow/dist/style.css';
import type { WorkflowState } from '../../../types/workflow';
import {
  NODE_HEIGHT,
  NODE_WIDTH,
  computeLayout,
  computedExitTriggers,
  guardKind,
  guardLabel,
  type EditableWorkflow,
  type GuardKind,
  type LayoutPosition,
} from '../../../utils/workflowDsl';

export type WorkflowSelection = { kind: 'state'; id: string } | { kind: 'edge'; id: string };

interface StateNodeData {
  state: WorkflowState;
  isEntry: boolean;
  isInterruptTarget: boolean;
  exitTriggers: string[];
  agentsCount: number;
  strategyRef: string | null;
}

export function countAgents(agents: unknown): number {
  if (!agents || typeof agents !== 'object') return 0;
  const a = agents as Record<string, unknown>;
  let n = 0;
  if (a.coordinator) n += 1;
  if (a.front) n += 1;
  if (a.validator) n += 1;
  if (Array.isArray(a.workers)) n += a.workers.length;
  return n;
}

const StateNode: React.FC<NodeProps<StateNodeData>> = ({ data, selected }) => {
  const { state } = data;
  const stage = state.stage != null && state.stage !== '' ? String(state.stage) : null;
  return (
    <div
      className={`cwf-node ${data.isEntry ? 'cwf-node--entry' : ''} ${selected ? 'cwf-node--selected' : ''}`}
      style={{ width: NODE_WIDTH, minHeight: NODE_HEIGHT }}
      data-testid={`cwf-node-${state.state_id}`}
    >
      <Handle id="t" type="target" position={Position.Top} className="cwf-handle" />
      <Handle id="l" type="target" position={Position.Left} className="cwf-handle" />
      <div className="cwf-node__head">
        <span className="cwf-node__id" title={state.state_id}>
          {state.state_id}
        </span>
        <span className="cwf-node__badges">
          {data.isEntry ? <span className="cwf-node__badge cwf-node__badge--entry">entry</span> : null}
          {data.isInterruptTarget ? (
            <span className="cwf-node__badge cwf-node__badge--interrupt" title="Target of a workflow interrupt — reachable without incoming edges">
              interrupt
            </span>
          ) : null}
        </span>
      </div>
      <div className="cwf-node__name" title={state.state_name}>
        {state.state_name || <em>unnamed</em>}
      </div>
      {state.primary_goal ? (
        <div className="cwf-node__goal" title={state.primary_goal}>
          {state.primary_goal}
        </div>
      ) : null}
      <div className="cwf-node__meta">
        {stage ? <span title="stage">stage {stage}</span> : null}
        {state.state_type ? <span title="state_type">{String(state.state_type)}</span> : null}
        {data.agentsCount > 0 ? (
          <span title="agents">
            <i className="fas fa-robot" aria-hidden="true" /> {data.agentsCount}
          </span>
        ) : null}
        {data.strategyRef ? (
          <span title="strategies_ref">
            <i className="fas fa-book" aria-hidden="true" /> {data.strategyRef}
          </span>
        ) : null}
      </div>
      {data.exitTriggers.length ? (
        <div className="cwf-node__exit" title={data.exitTriggers.join(', ')}>
          exit: {data.exitTriggers.join(', ')}
        </div>
      ) : null}
      <Handle id="r" type="source" position={Position.Right} className="cwf-handle cwf-handle--src" title="Drag to another state to add a transition" />
      <Handle id="b" type="source" position={Position.Bottom} className="cwf-handle cwf-handle--src" title="Drag to another state to add a transition" />
    </div>
  );
};

const nodeTypes = { wfState: StateNode };

export const EDGE_COLORS: Record<GuardKind, string> = {
  always: '#7F9AB3',
  card: '#FF8300',
  trigger: '#0099FA',
  flags_all: '#FF8300',
  flags_any: '#FF8300',
  expression: '#0099FA',
  empty: '#7F9AB3',
};

const HANDLE_PAIRS: Array<[string, string]> = [
  ['r', 'l'],
  ['b', 't'],
  ['r', 't'],
  ['b', 'l'],
];

export interface WorkflowCanvasProps {
  wf: EditableWorkflow;
  /** Bump to re-run the automatic layout for every node. */
  layoutKey: number;
  selection: WorkflowSelection | null;
  /** A state node click bubbles up here — the panel opens that state's full-page configurator, not just a side panel. */
  onSelect: (selection: WorkflowSelection | null) => void;
  onConnect: (source: string, target: string) => void;
}

const CanvasInner: React.FC<WorkflowCanvasProps> = ({ wf, layoutKey, selection, onSelect, onConnect }) => {
  const positionsRef = useRef<Record<string, LayoutPosition>>({});
  const layoutKeyRef = useRef<number>(-1);
  const [nodes, setNodes] = useNodesState<StateNodeData>([]);
  const [edges, setEdges] = useEdgesState([]);
  const { fitView, getViewport, setViewport } = useReactFlow();

  useEffect(() => {
    const ids = Object.keys(wf.states);

    if (layoutKeyRef.current !== layoutKey) {
      positionsRef.current = computeLayout(wf);
      layoutKeyRef.current = layoutKey;
      // Never zoom out below 0.55 to fit everything — a long LR chain becomes unreadable;
      // readable nodes plus panning beat a complete-but-microscopic overview.
      window.setTimeout(() => {
        fitView({ padding: 0.12, minZoom: 0.55, maxZoom: 1 });
        // When the graph overflows at the zoom floor, fitView centers it and pushes the
        // entry state off-canvas to the left — anchor the flow to its start instead.
        // fitView applies its transform asynchronously, so read the result a frame later.
        window.setTimeout(() => {
          const vp = getViewport();
          if (vp.x < 12) setViewport({ ...vp, x: 12 });
        }, 50);
      }, 60);
    } else {
      // New states appear to the right of everything that is already placed.
      const placed = Object.keys(positionsRef.current);
      let maxX = 0;
      placed.forEach((id) => {
        maxX = Math.max(maxX, positionsRef.current[id].x);
      });
      let stack = 0;
      ids.forEach((id) => {
        if (!positionsRef.current[id]) {
          positionsRef.current[id] = { x: maxX + NODE_WIDTH + 80, y: 40 + stack * (NODE_HEIGHT + 30) };
          stack += 1;
        }
      });
    }
    Object.keys(positionsRef.current).forEach((id) => {
      if (!wf.states[id]) delete positionsRef.current[id];
    });

    const interruptTargets: Record<string, true> = {};
    wf.interrupts.forEach((it) => {
      if (it.target_state_id) interruptTargets[it.target_state_id] = true;
    });

    const nextNodes: Node<StateNodeData>[] = ids.map((id) => {
      const state = wf.states[id];
      return {
        id,
        type: 'wfState',
        position: positionsRef.current[id] || { x: 0, y: 0 },
        selected: selection?.kind === 'state' && selection.id === id,
        data: {
          state,
          isEntry: wf.entry === id,
          isInterruptTarget: !!interruptTargets[id],
          exitTriggers: computedExitTriggers(wf, id).map((t) => t.name),
          agentsCount: countAgents(state.agents),
          strategyRef: state.strategies_ref?.name || null,
        },
      };
    });

    const pairSeen: Record<string, number> = {};
    const nextEdges: Edge[] = wf.edges.map((e) => {
      const pairKey = `${e.source}->${e.target}`;
      const dup = pairSeen[pairKey] || 0;
      pairSeen[pairKey] = dup + 1;
      const [sh, th] = HANDLE_PAIRS[Math.min(dup, HANDLE_PAIRS.length - 1)];
      const kind = guardKind(e.guard);
      const color = EDGE_COLORS[kind];
      const isSelected = selection?.kind === 'edge' && selection.id === e.id;
      const label = guardLabel(e.guard);
      return {
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: sh,
        targetHandle: th,
        type: 'smoothstep',
        label: label || undefined,
        labelStyle: { fontSize: 10, fill: '#4A6683', fontWeight: 500 },
        labelBgStyle: { fill: '#FFFFFF', fillOpacity: 0.92 },
        labelBgPadding: [5, 3],
        labelBgBorderRadius: 4,
        markerEnd: { type: MarkerType.ArrowClosed, color, width: 18, height: 18 },
        style: {
          stroke: color,
          strokeWidth: isSelected ? 3 : 1.7,
          strokeDasharray: kind === 'always' || e.type === 'manual' ? '6 4' : undefined,
        },
        animated: isSelected,
        selected: isSelected,
        interactionWidth: 18,
      };
    });

    setNodes(nextNodes);
    setEdges(nextEdges);
  }, [wf, layoutKey, selection, setNodes, setEdges, fitView, getViewport, setViewport]);

  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      // Positions are ours to remember; selection is driven by props.
      const positional = changes.filter((c) => c.type !== 'select');
      if (!positional.length) return;
      setNodes((current) => {
        const next = applyNodeChanges(positional, current);
        next.forEach((n) => {
          positionsRef.current[n.id] = n.position;
        });
        return next;
      });
    },
    [setNodes]
  );

  const handleConnect = useCallback(
    (c: Connection) => {
      if (!c.source || !c.target || c.source === c.target) return;
      onConnect(c.source, c.target);
    },
    [onConnect]
  );

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      onNodesChange={handleNodesChange}
      onEdgesChange={() => undefined}
      onConnect={handleConnect}
      onNodeClick={(_, node) => onSelect({ kind: 'state', id: node.id })}
      onEdgeClick={(_, edge) => onSelect({ kind: 'edge', id: edge.id })}
      onPaneClick={() => onSelect(null)}
      deleteKeyCode={null}
      multiSelectionKeyCode={null}
      selectionKeyCode={null}
      nodesConnectable
      elementsSelectable
      fitView
      fitViewOptions={{ padding: 0.12, minZoom: 0.55, maxZoom: 1 }}
      minZoom={0.15}
      maxZoom={2}
      proOptions={{ hideAttribution: true }}
    >
      <Background color="#E3F0FB" gap={18} />
      <Controls showInteractive={false} />
      <MiniMap
        pannable
        zoomable
        nodeColor={(n) => ((n.data as StateNodeData | undefined)?.isEntry ? '#0072C6' : '#C6E0F7')}
        maskColor="rgba(10, 42, 71, 0.06)"
      />
    </ReactFlow>
  );
};

const WorkflowCanvas: React.FC<WorkflowCanvasProps> = (props) => (
  <ReactFlowProvider>
    <CanvasInner {...props} />
  </ReactFlowProvider>
);

export default WorkflowCanvas;
